import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import '../../../../assets/css/student/dashboard/StudentProfile.css';
import StudentLayout from '../../../../layouts/student/StudentLayout';


/* ─────────────────────────────────────────
   Top Tab Navigation
───────────────────────────────────────── */
const KYCTabs = ({ activeStep, setActiveStep, completedSteps }) => {
  const tabs = [
    { step: 1, label: "Basic Info",  icon: "📋" },
    { step: 2, label: "Documents",   icon: "📂" },
    { step: 3, label: "KYC Status",  icon: "✅" },
  ];

  return (
    <div className="kyc-tabs">
      {tabs.map((tab) => {
        const isActive    = activeStep === tab.step;
        const isCompleted = completedSteps.includes(tab.step);
        const isDisabled  = tab.step > Math.max(activeStep, ...completedSteps, 1);

        return (
          <button
            key={tab.step}
            className={`kyc-tab
              ${isActive    ? "active"    : ""}
              ${isCompleted ? "completed" : ""}
              ${isDisabled  ? "disabled"  : ""}
            `}
            onClick={() => !isDisabled && setActiveStep(tab.step)}
          >
            <span className="kyc-tab-num">
              {isCompleted && !isActive ? "✓" : tab.step}
            </span>
            <span className="kyc-tab-label">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
};

/* ─────────────────────────────────────────
   Main KYC Component
───────────────────────────────────────── */
const KYCVerification = ({ isModal = false, onClose, onSuccess }) => {
  const [activeStep, setActiveStep]       = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState("");
  const [success, setSuccess]             = useState("");
  const [kycData, setKycData]             = useState(null);

  // (single combined file — no individual refs needed)

  // Step 1: Basic Info
  const [basicInfo, setBasicInfo] = useState({
    fullName:       "",
    fatherName:     "",
    cnicNumber:     "",
    dateOfBirth:    "",
    address:        "",
    instituteName:  "",
    educationLevel: "",
  });

  const [documents, setDocuments] = useState({
    combinedFile: null,
  });
  const [previews, setPreviews] = useState({});

  // ── Load Data ──
  useEffect(() => {
    loadStudentData();
    checkKYCStatus();
  }, []);

  const loadStudentData = () => {
    const stored = localStorage.getItem("student");
    if (stored) {
      try {
        const student = JSON.parse(stored);
        setBasicInfo((prev) => ({ ...prev, fullName: student.fullName || "" }));
      } catch (e) { console.error(e); }
    }
  };

  const checkKYCStatus = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res   = await axios.get("/api/student/kyc-status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success && res.data.kycData) {
        setKycData(res.data.kycData);
        if (res.data.kycData.verification_status !== 'pending') {
          setActiveStep(3);
          setCompletedSteps([1, 2, 3]);
        }
      }
    } catch (e) { console.error(e); }
  };

  // ── Handlers ──
  const handleBasicInfoChange = (e) => {
    const { name, value } = e.target;
    setBasicInfo((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleContinueStep1 = () => {
    setCompletedSteps((prev) => [...new Set([...prev, 1])]);
    setActiveStep(2);
    setError("");
  };

  const handleContinueStep2 = () => {
    handleSubmitKYC();
  };

  const handleSubmitKYC = async () => {
    setLoading(true);
    setError("");
    try {
      const token    = localStorage.getItem("authToken");
      const formData = new FormData();

      Object.entries(basicInfo).forEach(([key, val]) => formData.append(key, val));
      if (documents.combinedFile) formData.append("documents", documents.combinedFile);

      const res = await axios.post("/api/student/submit-kyc", formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        setCompletedSteps([1, 2, 3]);
        setActiveStep(3);
        setSuccess("KYC submitted! Your documents are under review.");
        setKycData({
          ...basicInfo,
          verification_status: "pending",
          submitted_at: new Date().toISOString(),
        });
        if (isModal && onSuccess) setTimeout(onSuccess, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit KYC. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (activeStep > 1) { setActiveStep(activeStep - 1); setError(""); }
  };

  const getStatusBadge = () => {
    if (!kycData) return null;
    const s = kycData.verification_status;
    if (s === "pending")  return <span className="kyc-status-badge status-pending">⏳ Under Review</span>;
    if (s === "approved") return <span className="kyc-status-badge status-approved">✅ Approved</span>;
    if (s === "rejected") return <span className="kyc-status-badge status-rejected">❌ Rejected</span>;
    return null;
  };

  const handleModalClose = () => { if (isModal && onClose) onClose(); };

  /* ── RENDER ── */
  return (
    <StudentLayout>
      <div className="kyc-container">

        {/* Header */}
        <div className="kyc-header">
          <div>
            <h2>KYC Verification</h2>
            <p className="kyc-header-subtitle">Complete your verification to apply for donations</p>
          </div>
          <div className="kyc-header-actions">
            {getStatusBadge()}
            {isModal && (
              <button className="kyc-close-btn" onClick={handleModalClose}>×</button>
            )}
          </div>
        </div>

        {/* ── TOP TABS ── */}
        <KYCTabs
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          completedSteps={completedSteps}
        />

        {/* Alerts */}
        {error   && <div className="kyc-alert kyc-alert-error">  <span>⚠️</span> {error}   </div>}
        {success && <div className="kyc-alert kyc-alert-success"><span>✓</span>  {success}  </div>}

        {/* ── Content Card ── */}
        <div className="kyc-content">

          {/* ══ TAB 1: Basic Information ══ */}
          {activeStep === 1 && (
            <div className="kyc-step-content">
              <h3>📋 Basic Information</h3>
              <p className="kyc-step-subtitle">Please provide your personal details accurately</p>

              <div className="kyc-form-grid">
                <div className="kyc-form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    value={basicInfo.fullName}
                    onChange={handleBasicInfoChange}
                    className="kyc-input"
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="kyc-form-group">
                  <label>Father Name</label>
                  <input
                    type="text"
                    name="fatherName"
                    value={basicInfo.fatherName}
                    onChange={handleBasicInfoChange}
                    className="kyc-input"
                    placeholder="Enter father's name"
                  />
                </div>

                <div className="kyc-form-group kyc-form-group-full">
                  <label>Address</label>
                  <textarea
                    name="address"
                    value={basicInfo.address}
                    onChange={handleBasicInfoChange}
                    className="kyc-input"
                    placeholder="Enter your complete address"
                    rows="3"
                  />
                </div>

                <div className="kyc-form-group kyc-form-group-full">
                  <label>Phone</label>
                  <input
                    name="phone"
                    value={basicInfo.phone}
                    onChange={handleBasicInfoChange}
                    className="kyc-input"
                    placeholder="Enter your Phone number"
                  />
                </div>

                <div className="kyc-form-group">
                  <label>Institute Name</label>
                  <input
                    type="text"
                    name="instituteName"
                    value={basicInfo.instituteName}
                    onChange={handleBasicInfoChange}
                    className="kyc-input"
                    placeholder="Enter your institute name"
                  />
                </div>

                <div className="kyc-form-group">
                  <label>Education Level</label>
                  <select
                    name="educationLevel"
                    value={basicInfo.educationLevel}
                    onChange={handleBasicInfoChange}
                    className="kyc-select"
                  >
                    <option value="">Select Education Level</option>
                    <option value="Matric">Matric (9th–10th)</option>
                    <option value="Intermediate">Intermediate (11th–12th)</option>
                    <option value="Bachelor">Bachelor Degree</option>
                    <option value="Master">Master Degree</option>
                    <option value="Diploma">Diploma / Certificate</option>
                  </select>
                </div>
              </div>

              <div className="kyc-note-box">
                <p><strong>📌 Important:</strong> The NGO will verify this information. Please ensure all details are accurate and match your official documents.</p>
              </div>

              <div className="kyc-button-group">
                <button className="kyc-btn-primary" onClick={handleContinueStep1}>
                  Continue to Documents →
                </button>
              </div>
            </div>
          )}

          {/* ══ TAB 2: Documents ══ */}
          {activeStep === 2 && (
            <div className="kyc-step-content">
              <h3>📂 Document Upload</h3>
              <p className="kyc-step-subtitle">Upload all your required documents in a single PDF file</p>

              {/* Required Documents List */}
              <div className="kyc-doc-instruction-banner">
                <h4>📎 Include the following documents in your PDF:</h4>
                <div className="kyc-doc-list">
                  <div className="kyc-doc-list-item required">
                    <span className="kyc-doc-num">1</span>
                    <div>
                      <strong>CNIC / B-Form (Front Side)</strong>
                      <p>Front side of your national identity card must be clearly visible</p>
                    </div>
                    <span className="kyc-required-badge">Required</span>
                  </div>
                  <div className="kyc-doc-list-item required">
                    <span className="kyc-doc-num">2</span>
                    <div>
                      <strong>CNIC / B-Form (Back Side)</strong>
                      <p>Back side of your national identity card must be clearly visible</p>
                    </div>
                    <span className="kyc-required-badge">Required</span>
                  </div>
                  <div className="kyc-doc-list-item required">
                    <span className="kyc-doc-num">3</span>
                    <div>
                      <strong>Student ID Card</strong>
                      <p>Your current student ID card issued by your institute</p>
                    </div>
                    <span className="kyc-required-badge">Required</span>
                  </div>
                  <div className="kyc-doc-list-item optional">
                    <span className="kyc-doc-num">4</span>
                    <div>
                      <strong>Latest Fee Slip</strong>
                      <p>Most recent fee receipt or deposit challan</p>
                    </div>
                    <span className="kyc-optional-badge">Optional</span>
                  </div>
                </div>
              </div>

              {/* Single File Upload */}
              <div className="kyc-single-upload-card">
                <div className="kyc-upload-header">
                  <h4>📁 Upload All Documents as a Single PDF File</h4>
                </div>
                {documents.combinedFile ? (
                  <div className="kyc-single-file-preview">
                    <div className="kyc-file-icon">📄</div>
                    <div className="kyc-file-info">
                      <strong>{documents.combinedFile.name}</strong>
                      <span>{(documents.combinedFile.size / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    <button className="kyc-preview-remove" onClick={() => setDocuments((prev) => ({ ...prev, combinedFile: null }))}>×</button>
                  </div>
                ) : (
                  <div className="kyc-upload-box kyc-upload-box-large" onClick={() => document.getElementById('combinedFileInput').click()}>
                    <div className="kyc-upload-icon-large">☁️</div>
                    <p className="kyc-upload-main-text">Click here or drag your file</p>
                    <p className="kyc-upload-sub-text">PDF, JPG, PNG — Max 10MB</p>
                  </div>
                )}
                <input
                  id="combinedFileInput"
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) setDocuments((prev) => ({ ...prev, combinedFile: file }));
                  }}
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  hidden
                />
              </div>

              <div className="kyc-guidelines">
                <h4>📌 Upload Guidelines</h4>
                <ul>
                  <li>✓ Scan all documents into a single PDF before uploading</li>
                  <li>✓ Maximum file size: 10MB</li>
                  <li>✓ Accepted formats: PDF, JPG, PNG</li>
                  <li>✓ All documents must be clearly readable</li>
                </ul>
              </div>

              <div className="kyc-button-group">
                <button className="kyc-btn-secondary" onClick={handleBack}>← Back</button>
                <button className="kyc-btn-primary" onClick={handleContinueStep2} disabled={loading}>
                  {loading ? "Submitting…" : "Submit Documents →"}
                </button>
              </div>
            </div>
          )}

          {/* ══ TAB 3: KYC Status ══ */}
          {activeStep === 3 && (
            <div className="kyc-step-content kyc-review-step">
              <div className="kyc-status-icon">
                {kycData?.verification_status === "approved" && <div className="status-icon-approved">✓</div>}
                {kycData?.verification_status === "pending"  && <div className="status-icon-pending">⏳</div>}
                {kycData?.verification_status === "rejected" && <div className="status-icon-rejected">✗</div>}
              </div>

              <h3>
                {kycData?.verification_status === "approved" && "KYC Approved!"}
                {kycData?.verification_status === "pending"  && "KYC Under Review"}
                {kycData?.verification_status === "rejected" && "KYC Rejected"}
              </h3>

              <p className="kyc-status-message">
                {kycData?.verification_status === "approved" && "Your account is verified. You can now apply for donations."}
                {kycData?.verification_status === "pending"  && "Your documents are being reviewed by our admin team. This usually takes 24–48 hours."}
                {kycData?.verification_status === "rejected" && "Your KYC was rejected. Please review the reason below and resubmit."}
              </p>

              {kycData?.verification_status === "rejected" && kycData?.rejection_reason && (
                <div className="kyc-rejection-reason">
                  <h4>Rejection Reason:</h4>
                  <p>{kycData.rejection_reason}</p>
                </div>
              )}

              <div className="kyc-review-details">
                <h4>📋 Submitted Information</h4>
                <div className="kyc-detail-grid">
                  <div className="kyc-detail-item">
                    <label>Full Name</label>
                    <span>{kycData?.fullName || basicInfo.fullName || "—"}</span>
                  </div>
                  <div className="kyc-detail-item">
                    <label>Father Name</label>
                    <span>{kycData?.fatherName || basicInfo.fatherName || "—"}</span>
                  </div>
                  <div className="kyc-detail-item kyc-detail-item-full">
                    <label>Address</label>
                    <span>{kycData?.address || basicInfo.address || "—"}</span>
                  </div>
                  <div className="kyc-detail-item">
                    <label>Institute</label>
                    <span>{kycData?.instituteName || basicInfo.instituteName || "—"}</span>
                  </div>
                  <div className="kyc-detail-item">
                    <label>Education Level</label>
                    <span>{kycData?.educationLevel || basicInfo.educationLevel || "—"}</span>
                  </div>
                  <div className="kyc-detail-item">
                    <label>Submitted On</label>
                    <span>
                      {kycData?.submitted_at
                        ? new Date(kycData.submitted_at).toLocaleDateString("en-PK")
                        : new Date().toLocaleDateString("en-PK")}
                    </span>
                  </div>
                  <div className="kyc-detail-item">
                    <label>Status</label>
                    {getStatusBadge()}
                  </div>
                </div>
              </div>

              <div className="kyc-button-group">
                {kycData?.verification_status === "rejected" && (
                  <button className="kyc-btn-primary" onClick={() => { setActiveStep(1); setCompletedSteps([]); }}>
                    Resubmit KYC
                  </button>
                )}
                {isModal && (
                  <button className="kyc-btn-secondary" onClick={handleModalClose}>Close</button>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </StudentLayout>
  );
};

export default KYCVerification;